import type { Locale } from '@/i18n';
import type { OnlineOrder } from '@restaurant/api-client';
import type { Restaurant } from '@restaurant/api-contracts';

const EARTH_RADIUS_KM = 6371;

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle (straight-line) distance in km between two lat/lng points. */
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.asin(Math.sqrt(a));
}

/**
 * Straight-line distance from the restaurant to a delivery order's drop point,
 * in km — or `null` when it can't be computed: the order isn't a delivery, it
 * carries no delivery address, or the restaurant has no pinned location
 * (`latitude`/`longitude` are nullable — legacy / map-pin-not-set setups).
 *
 * This is the crow-flies distance for a quick "how far away" read, NOT the
 * driving distance the backend uses to price the delivery fee.
 */
export function deliveryDistanceKm(
  order: OnlineOrder,
  restaurant: Restaurant | null,
): number | null {
  if (order.type !== 'DELIVERY' || !order.deliveryAddress) return null;
  if (restaurant?.latitude == null || restaurant.longitude == null) return null;
  return haversineKm(
    restaurant.latitude,
    restaurant.longitude,
    order.deliveryAddress.latitude,
    order.deliveryAddress.longitude,
  );
}

/**
 * Distance from the restaurant to a delivery drop point, in km, preferring the
 * snapshotted Mapbox driving distance (road km) and falling back to the
 * straight-line estimate when no route was captured. Returns `null` under the
 * same conditions as {@link deliveryDistanceKm}.
 *
 * Use this for the "how far away" read shown to the owner so the list row and
 * the detail panel always agree — road distance where we have it, crow-flies
 * only as a last resort.
 */
export function deliveryDistanceKmPreferRoute(
  order: OnlineOrder,
  restaurant: Restaurant | null,
): number | null {
  const route = order.deliveryAddress?.route;
  if (route) return route.distanceMeters / 1000;
  return deliveryDistanceKm(order, restaurant);
}

/** Localized one-decimal distance with unit, e.g. "3.2 km" (de → "3,2 km").
 *  Under 1 km rounds to whole hundreds of metres so it never reads "0.0 km". */
export function formatDistanceKm(km: number, locale: Locale): string {
  const value = new Intl.NumberFormat(locale, {
    minimumFractionDigits: km < 10 ? 1 : 0,
    maximumFractionDigits: 1,
  }).format(km);
  return `${value} km`;
}
